"use client";

import { throttle } from "lodash";
import { useLayoutEffect, useRef, useState } from "react";

type UseIsOverflowing = (
  text: string,
  options?: { throttleTime?: number },
) => {
  isOverflowing: boolean;
  textRef: React.RefObject<HTMLParagraphElement | null>;
};

export const useIsOverflowing: UseIsOverflowing = (text, { throttleTime = 1000 } = {}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to re-run the effect when the text changes
  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const updateOverflow = throttle(() => {
      setIsOverflowing(element.offsetWidth < element.scrollWidth);
    }, throttleTime);

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(element);

    // Initial check
    updateOverflow();

    return () => {
      resizeObserver.disconnect();
      updateOverflow.cancel();
    };
  }, [text, throttleTime]);

  return { isOverflowing, textRef };
};
